/**
 * What the Measurements panel lists — `docs/property-maps-v2.md` §5.4, §5.6.
 *
 * Two sources, one list. **Saved parts** come from the server, grouped by
 * measurement type with the total a quote prices from; **unsaved rows** are
 * shapes on the measure tape that nobody has pressed Add on yet. The panel
 * draws them together because they answer one question — *what has this site
 * been measured at* — and keeping them apart is how somebody saves eight of
 * nine and never notices the ninth.
 *
 * It is pure, so the group header's arithmetic (`Hardscape · 640 sq ft
 * + 640 new`) can be tested without a canvas, a map or a server.
 */
import { LAYER_PALETTE, measuresOf, unitFamily, type LayerColor } from '@rowted/shared'
import type { MeasurementGroup, MeasurementPart } from '../api.js'
import type { MeasureShape } from '../measure-session.js'

export interface PanelRow {
  /** The part id where it is saved; the shape's id where it is not. */
  id: string
  name: string
  value: number
  /** `Patio / hardscape · Walked · A. Kline · Sep 10`, already assembled. */
  detail: string
  saved: boolean
  /** The shape it came from, where there is one — the *map changed* check. */
  featureId: string | null
  mapChanged: boolean
  /** A part whose shape was deleted keeps its number and says so. */
  shapeDeleted: boolean
}

export interface PanelGroup {
  typeId: string
  type: string
  unit: string
  /** What the property holds now — the number a quote reads. */
  saved: number | null
  /** What pressing *Save to property* would add. Zero means nothing waiting. */
  waiting: number
  rows: PanelRow[]
}

/**
 * The same type is the same colour, everywhere it is drawn.
 *
 * `measurement_types` has no colour column and this does not add one: a dot
 * beside a row is not worth a migration, and a colour nobody can edit is
 * better derived than stored. From the **name** rather than the id, for the
 * reason `avatarTone` is: the id is not always to hand and a dot that changed
 * between two screens would be worse than no dot.
 */
export function typeColour(name: string): string {
  let hash = 2166136261
  for (let index = 0; index < name.length; index += 1) {
    hash ^= name.charCodeAt(index)
    hash = Math.imul(hash, 16777619)
  }
  const palette = Object.keys(LAYER_PALETTE) as LayerColor[]
  return LAYER_PALETTE[palette[Math.abs(hash) % palette.length]!]
}

/** What a shape on the tape measures, in its type's own unit family. */
export function valueOf(shape: MeasureShape, unit: string): number {
  const { areaSqft, lengthFt } = measuresOf(shape.kind, shape.geometry)
  return (unitFamily(unit) === 'area' ? areaSqft : lengthFt) ?? 0
}

/** `Turf · Aerial · J. Reyes · Mar 2026`, with the empty parts dropped. */
function detailOf(parts: (string | null | undefined)[]): string {
  return parts.filter((one): one is string => Boolean(one)).join(' · ')
}

const SOURCE_WORDS: Record<string, string> = {
  walked: 'Walked',
  aerial: 'Aerial',
  typed: 'Typed',
  map_layer: 'From map',
  mixed: 'Mixed sources',
}

/** What a part's source is called on screen (§10 adds the last two). */
export const sourceWords = (source: string): string => SOURCE_WORDS[source] ?? source

function rowOfPart(part: MeasurementPart, type: string, layerName?: string): PanelRow {
  return {
    id: part.id,
    name: part.name,
    value: part.value,
    detail: detailOf([
      type,
      part.source === 'map_layer' && layerName
        ? `From map · ${layerName}`
        : sourceWords(part.source),
      part.measuredByName,
      part.measuredOn,
    ]),
    saved: true,
    featureId: part.featureId,
    mapChanged: part.mapChanged,
    shapeDeleted: part.shapeDeleted,
  }
}

/**
 * Fold the saved groups and the tape into the panel's list.
 *
 * A type with nothing on either side is absent: §10's rule that a catalogue
 * of a dozen types is not a site with a dozen missing measurements.
 *
 * **`types` is what lets the first outline on a site show up.** The saved
 * groups only contain types somebody has already measured, so a brand new
 * tape shape filed under Turf on a property with no Turf total had no group
 * to join and vanished — along with the footer's *Save to property*, which
 * left the popup as the only way to save anything. A type named by a tape
 * shape gets a group with `saved: null`, which is the truth: nothing is on
 * the property yet.
 */
export function panelGroups(
  groups: MeasurementGroup[],
  tape: MeasureShape[],
  layerNames?: Record<string, string>,
  types?: { measurementTypeId: string; name: string; unit: string }[],
): PanelGroup[] {
  const byType = new Map<string, PanelGroup>()

  for (const group of groups) {
    byType.set(group.typeId, {
      typeId: group.typeId,
      type: group.type,
      unit: group.unit,
      saved: group.total,
      waiting: 0,
      rows: group.parts.map((part) => rowOfPart(
        part, group.type,
        part.featureId ? layerNames?.[part.featureId] : undefined,
      )),
    })
  }

  for (const shape of tape) {
    if (shape.saved || !shape.typeId) continue
    let group = byType.get(shape.typeId)
    if (!group) {
      const type = types?.find((one) => one.measurementTypeId === shape.typeId)
      if (!type) continue
      group = {
        typeId: type.measurementTypeId,
        type: type.name,
        unit: type.unit,
        saved: null,
        waiting: 0,
        rows: [],
      }
      byType.set(type.measurementTypeId, group)
    }
    const value = valueOf(shape, group.unit)
    group.waiting += value
    group.rows.push({
      id: shape.id,
      name: shape.name,
      value,
      detail: detailOf([group.type, 'Aerial', 'today']),
      saved: false,
      featureId: null,
      mapChanged: false,
      shapeDeleted: false,
    })
  }

  return [...byType.values()].filter((group) => group.rows.length > 0)
}

/**
 * §6.4's Measurements sub-line: `8 on this property · 2 not saved yet`.
 *
 * The second half drops out when there is nothing waiting rather than
 * reading `· 0 not saved yet`, which is a nought somebody has to parse to
 * learn that nothing happened. It lives here beside `panelGroups` because
 * it is the same question that panel's footer answers, and because a string
 * a test can reach should not be inside the editor.
 */
export function measureWords(saved: number, waiting: number): string {
  const first = `${saved} on this property`
  return waiting > 0 ? `${first} · ${waiting} not saved yet` : first
}
